<template>
  <div class="my-10">
    <div class="flex flex-col gap-10">
      <div class="grid grid-cols-2 gap-5 max-lg:grid-cols-1">
        <div class="flex flex-col">
          <div>
            <video
              controlsList="nodownload"
              v-if="isVideo(currentImage)"
              controls
              :src="currentImage"
              class="object-contain h-[450px] w-full"
            ></video>
            <img
              v-else
              @click="showSingle"
              :src="currentImage || placeholderImage"
              alt="main-image"
              class="w-full h-[25rem] object-fill rounded-lg cursor-pointer"
            />
            <vue-easy-lightbox
              :imgs="currentImage"
              :index="indexRef"
              :visible="visibleRef"
              @hide="onHide"
            ></vue-easy-lightbox>
          </div>
          <div v-if="this.detail?.images?.length">
            <Splide :has-track="false" :options="imageOptions" aria-label="carouselBanner" class="">
              <SplideTrack class="rounded-lg py-2">
                <SplideSlide v-for="(item, index) in detail.images" :key="item.id" class="">
                  <video
                    @click="changeImage(index)"
                    controlsList="nodownload"
                    v-if="isVideo(item?.path)"
                    :src="item?.path"
                    class="h-32 object-cover rounded-md cursor-pointer w-full"
                  ></video>

                  <img
                    v-else
                    :src="item?.path || placeholderImage"
                    alt=""
                    class="h-32 w-full object-cover rounded-md cursor-pointer"
                    @click="changeImage(index)"
                  />
                </SplideSlide>
              </SplideTrack>
            </Splide>
          </div>
        </div>
        <div>
          <div class="border-b border-[#E3E3E3] mb-4">
            <div class="flex items-center gap-3 mb-3 min-w-0">
              <p
                v-if="detail?.name"
                class="flex-1 min-w-0 text-main-color font-bold text-xl truncate"
                :title="detail?.name"
              >
                {{ detail?.name }}
              </p>

              <p
                v-else
                class="flex-1 min-w-0 text-main-color font-bold text-xl truncate"
                :title="(detail?.title_properties || []).map(property => property.value).join(' ')"
              >
                <span v-for="(property, idx) in detail?.title_properties" :key="idx">
                  {{ property.value }}
                </span>
              </p>
            </div>
            <div class="mb-2">
              <p>
                {{ $t('page.category') }}:
                <router-link
                  class="text-lg font-medium hover:text-accent-color"
                  :to="{ name: 'publications', query: { category: detail?.category_id } }"
                  >{{ detail?.category }}</router-link
                >
              </p>
            </div>
            <div class="flex items-center gap-5 mb-3">
              <p class="text-[#409DBA] font-semibold text-2xl">
                {{ formatNumber(detail?.price) }} TMT
              </p>
            </div>

            <div class="flex mb-2 font-light text-main-color text-base">
              <p>{{ detail?.location }}</p>
              <div class="flex items-center">
                <div class="border-l mx-2 h-5"></div>
                <p>{{ detail?.date_created }}</p>
              </div>
            </div>
            <div class="mb-2">
              <a class="hover:text-accent-color" :href="`tel:${detail?.phone}`">{{
                detail?.phone
              }}</a>
            </div>
          </div>

          <div class="w-full">
            <p v-if="detail?.status !== undefined" class="font-bold mb-3">
              {{ $t('page.status') }}:
              <span :class="statusView.class">
                {{ statusView.label }}
              </span>
            </p>
            <p v-if="rejectionReasonText" class="font-bold mb-3">
              {{ $t('page.reason') }}:
              <span class="text-red-500 font-medium" v-html="rejectionReasonText"></span>
            </p>
            <p></p>
            <div class="flex flex-col gap-3 mb-4">
              <div v-if="categoryDetail?.on_credit" class="flex items-center gap-2">
                {{ $t('page.onCredit') }} <IconChecked v-if="detail?.on_credit" />
                <IconUnchecked v-else />
              </div>
              <div v-if="categoryDetail?.on_exchange" class="flex items-center gap-2">
                {{ $t('page.onExchange') }} <IconChecked v-if="detail?.on_exchange" />
                <IconUnchecked v-else />
              </div>
              <div class="flex items-center gap-2">
                {{ $t('page.allowComments') }} <IconChecked v-if="detail?.allow_comments" />
                <IconUnchecked v-else />
              </div>
            </div>
            <p class="font-light text-[#333333] text-lg w-96 mb-5">
              {{ detail?.description }}
            </p>
            <div class="flex justify-end gap-5 mb-3">
              <button
                v-if="detail?.subscription !== ''"
                class="w-max rounded-lg px-8 font-bold"
                :class="getSubcriptionType?.color"
              >
                {{ getSubcriptionType?.name }}
              </button>
              <div
                v-if="detail?.is_vip"
                class="w-max px-8 bg-[#7951F9] rounded-lg text-white font-bold text-2xl text-end"
              >
                VIP
              </div>
            </div>
            <div class="bg-white divide-y rounded mb-5">
              <p class="text-main-color text-base font-semibold p-4">{{ $t('page.detail') }}</p>
              <InfoBlock :attributes="detail?.attributes" />
            </div>

            <button
              @click="deletePublication"
              class="bg-red-500 text-white w-full rounded py-3 hover:bg-red-600"
            >
              {{ $t('page.remove') }}
            </button>
          </div>
        </div>
      </div>
      <!--  -->
    </div>
  </div>
</template>
<script>
import api from '@/api';
import VueEasyLightbox from 'vue-easy-lightbox';
import IconChecked from '@icons/IconChecked.vue';
import IconUnchecked from '@icons/IconUnchecked.vue';
import { i18n } from '@/i18n';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import { formatDate, formattedHour, formatNumber } from '@/helpers';
import { MYPUBLICATION_STATUSES } from '@/constants';
import { isVideo } from '@/helpers';
import InfoBlock from '@cmp/InfoBlock.vue';

export default {
  name: 'viewMyBusinessPublicationRequest',
  components: {
    VueEasyLightbox,
    Splide,
    SplideSlide,
    SplideTrack,
    IconChecked,
    IconUnchecked,
    InfoBlock,
  },
  data() {
    return {
      currentId: null,
      detail: null,
      visibleRef: false,
      indexRef: 0,
      imgsRef: [],
      currentImage: null,
      MYPUBLICATION_STATUSES: MYPUBLICATION_STATUSES,
      categoryDetail: {},
      placeholderImage: '/placeholder-publication.png',
    };
  },
  methods: {
    isVideo,
    formatDate,
    formattedHour,
    formatNumber,
    onShow() {
      this.visibleRef = true;
      this.imgsRef = this.detail.images;
    },
    onHide() {
      this.visibleRef = false;
    },
    showSingle() {
      if (!this.detail?.images?.length) return;
      this.onShow();
    },
    changeImage(index) {
      this.currentImage =
        this.detail?.images?.[index]?.path || this.detail?.thumbnail || this.placeholderImage;
    },

    async getMyPublicationRequest() {
      try {
        const response = await api.get(`publications/my-publication-requests/${this.currentId}/`);
        this.detail = response.data;

        if (this.detail?.images?.length > 0) {
          this.currentImage =
            this.detail?.images[0]?.path || this.detail?.thumbnail || this.placeholderImage;
        } else {
          this.currentImage = this.detail?.thumbnail || this.placeholderImage;
        }

        await this.getCategoryDetail();
      } catch (e) {
        console.log(e);
      }
    },

    async getCategoryDetail() {
      try {
        const response = await api.get(`publications/categories/${this.detail.category_id}/`);
        this.categoryDetail = response.data;
      } catch (e) {
        console.log(e);
      }
    },
    async deletePublication() {
      try {
        const response = await api.delete(
          `publications/my-publication-requests/${this.currentId}/`,
        );
        this.$toast.info(i18n.global.t('warning.PUBLICATION_REMOVED'));
        this.$router.push({ name: 'businessPublicationRequests' });
      } catch (e) {
        console.log(e);
      }
    },
  },
  computed: {
    getSubcriptionType() {
      switch (this.detail?.subscription) {
        case 'Gold':
          return { color: 'bg-[#FABB18] text-black', name: 'Gold' };
        case 'Silver':
          return { color: 'bg-[#C0C0C0] text-black', name: 'Silver' };
        case 'Bronze':
          return { color: 'bg-[#AE7145] text-white', name: 'Bronze' };
        default:
          break;
      }
    },

    rejectionReasonText() {
      return this.detail?.standard_rejection_reason || this.detail?.rejection_reason || '';
    },

    statusView() {
      const locale = this.$i18n?.locale || 'ru';

      if (this.rejectionReasonText) {
        return {
          class: 'text-red-500',
          label: locale === 'tk' ? 'Ret edildi' : 'Отклонён',
        };
      }

      const fallback = this.MYPUBLICATION_STATUSES?.[this.detail?.status];
      return {
        class: fallback?.class || 'text-yellow-500',
        label: fallback?.label?.[locale] || (locale === 'tk' ? 'Garaşylýar' : 'Ожидается'),
      };
    },
  },

  created() {
    this.currentId = this.$route.params.id;
    this.getMyPublicationRequest();
  },
};
</script>

<style scoped></style>
